import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import type {
  GrantClosedMode,
  GrantListItemDto,
  GrantSort,
  GrantSupportFilter,
  Paginated,
  ProgramKind,
} from '@zoxo/shared';

import { api } from '../../lib/api';

export interface GrantFilters {
  region?: string;
  field?: string;
  /** 기둥. 「금액 확인된 것만」을 대신한다. */
  support?: GrantSupportFilter;
  /** 프로그램 하위 종류. `support = program` 일 때만 뜻이 있다 (REFACTOR §6). */
  kind?: ProgramKind;
  /** 마감 공고 취급. 기본은 제외. */
  closed?: GrantClosedMode;
  sort?: GrantSort;
  /** 검색어. 제목·기관명 부분일치. */
  q?: string;
}

/** 필터를 쿼리키·쿼리스트링 양쪽에서 같은 순서로 쓰기 위한 정규화. */
function toParams(f: GrantFilters, cursor?: string): string {
  const p = new URLSearchParams();
  if (f.region) p.set('region', f.region);
  if (f.field) p.set('field', f.field);
  if (f.support) p.set('support', f.support);
  if (f.kind) p.set('kind', f.kind);
  if (f.closed && f.closed !== 'exclude') p.set('closed', f.closed);
  if (f.sort && f.sort !== 'latest') p.set('sort', f.sort);
  if (f.q) p.set('q', f.q);
  if (cursor) p.set('cursor', cursor);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/**
 * 지원사업 목록 (GET /grants). 목록 loader(list-loader.ts)와 훅이 같은 옵션을 쓴다 —
 * 캐시 키를 손으로 맞출 일이 없다.
 *
 * 무한 스크롤이라 useSuspenseQuery 가 아니라 useInfiniteQuery 를 쓴다 —
 * 기존 목록(기사·서베이)과 같은 예외다. 커서는 불투명하며 API 가 준 값을
 * 그대로 되돌려 준다.
 */
export function grantsQuery(filters: GrantFilters) {
  return infiniteQueryOptions({
    queryKey: [
      'grants',
      filters.region ?? null,
      filters.field ?? null,
      filters.support ?? null,
      filters.closed ?? 'exclude',
      filters.sort ?? 'latest',
      filters.q ?? null,
      filters.kind ?? null,
    ],
    queryFn: ({ pageParam }) =>
      api.get<Paginated<GrantListItemDto>>(`/grants${toParams(filters, pageParam)}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  });
}

export function useGrants(filters: GrantFilters) {
  return useInfiniteQuery(grantsQuery(filters));
}
